import { useEffect, useState, type FormEvent } from "react";
import { BiometricSecuritySection } from "../components/BiometricSecuritySection";
import { PasswordField } from "../components/PasswordField";
import { EmailInput, OtpInput } from "../components/MobileFields";
import { SmtpSettingsForm } from "../components/SmtpSettingsForm";
import { IconChevron, IconCode, IconDocs, IconKey, IconLock, IconLogs, IconMail, IconOverview, IconSdk, IconUsers } from "../components/icons";
import { type Developer, PASSWORD_MIN_LENGTH, devApi, passwordRequirementText, validatePassword } from "../developer/api";
import { updateNativeEmail, updateNativePassword } from "../lib/native-bridge";

function PasswordForm({ onPasswordUpdated }: { onPasswordUpdated?: (password: string) => void }) {
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  const submit = async (ev: FormEvent) => {
    ev.preventDefault();
    setError(null);
    setOk(false);
    if (next !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    const invalid = validatePassword(next);
    if (invalid) {
      setError(invalid);
      return;
    }
    setBusy(true);
    try {
      await devApi("/api/developers/password", {
        method: "POST",
        body: JSON.stringify({ new_password: next }),
      });
      setNext("");
      setConfirm("");
      setOk(true);
      onPasswordUpdated?.(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not change password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <p className="devportal-hint">{passwordRequirementText()}</p>
      <form className="device-form" onSubmit={(ev) => void submit(ev)}>
        <label>
          New password (min {PASSWORD_MIN_LENGTH})
          <PasswordField autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} minLength={PASSWORD_MIN_LENGTH} required autoFocus />
        </label>
        <label>
          Confirm new password
          <PasswordField autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} minLength={PASSWORD_MIN_LENGTH} required />
        </label>
        {error && <p className="form-error">{error}</p>}
        {ok && <p className="devportal-hint">Password updated.</p>}
        <button type="submit" className="primary" disabled={busy}>
          {busy ? "Saving…" : "Update password"}
        </button>
      </form>
    </>
  );
}

type EmailStep = "current" | "current_otp" | "new_email" | "new_otp" | "confirm" | "done";

type OtpSendResult = {
  ok: boolean;
  expires_in: number;
  resend_in: number;
  masked_email: string;
  mail_sent?: boolean;
};

function formatMmSs(sec: number): string {
  const m = Math.floor(Math.max(0, sec) / 60);
  const s = Math.max(0, sec) % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function useCountdown(untilMs: number | null): number {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (!untilMs) {
      setLeft(0);
      return;
    }
    const tick = () => setLeft(Math.max(0, Math.ceil((untilMs - Date.now()) / 1000)));
    tick();
    const id = window.setInterval(tick, 250);
    return () => window.clearInterval(id);
  }, [untilMs]);
  return left;
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function EmailForm({
  me,
  onAccountUpdated,
  onDone,
}: {
  me: Developer;
  onAccountUpdated?: (developer: Developer) => void;
  onDone?: () => void;
}) {
  const [step, setStep] = useState<EmailStep>("current");
  const [otp, setOtp] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [updatedEmail, setUpdatedEmail] = useState(me.email);
  const [ticket, setTicket] = useState("");
  const [confirmTicket, setConfirmTicket] = useState("");
  const [masked, setMasked] = useState(me.email);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [resendAt, setResendAt] = useState<number | null>(null);

  const currentEmail = (updatedEmail || me.email || "").trim();
  const expiresIn = useCountdown(expiresAt);
  const resendIn = useCountdown(resendAt);

  useEffect(() => {
    const next = (me.email || "").trim();
    if (!next) return;
    if (step === "current" || step === "done") setUpdatedEmail(next);
  }, [me.email, step]);

  useEffect(() => {
    if (step !== "done" || !onDone) return;
    const id = window.setTimeout(() => onDone(), 1600);
    return () => window.clearTimeout(id);
  }, [step, onDone]);

  const rememberSend = (body: OtpSendResult) => {
    setMasked(body.masked_email || masked);
    setExpiresAt(Date.now() + body.expires_in * 1000);
    setResendAt(Date.now() + body.resend_in * 1000);
    if (body.mail_sent === false) {
      setOk("Code created. SMTP is not configured, so check the API server logs for the OTP.");
    } else {
      setOk(`Code sent to ${body.masked_email}.`);
    }
  };

  const sendCurrentOtp = async () => {
    if (!currentEmail) {
      setError("Your current email could not be loaded. Sign in again.");
      return;
    }
    setBusy(true);
    setError(null);
    setOk(null);
    try {
      const body = await devApi<OtpSendResult>("/api/developers/email/otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      rememberSend(body);
      setOtp("");
      setStep("current_otp");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not send verification code");
    } finally {
      setBusy(false);
    }
  };

  const resendCurrentOtp = async () => {
    setBusy(true);
    setError(null);
    setOk(null);
    try {
      const body = await devApi<OtpSendResult>("/api/developers/email/otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      rememberSend(body);
      setOtp("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not send verification code");
    } finally {
      setBusy(false);
    }
  };

  const verifyCurrentOtp = async (ev: FormEvent) => {
    ev.preventDefault();
    const code = otp.replace(/\D/g, "");
    if (code.length !== 6) {
      setError("Enter the 6-digit verification code.");
      return;
    }
    if (expiresIn <= 0) {
      setError("That code has expired. Request a new one.");
      return;
    }
    setBusy(true);
    setError(null);
    setOk(null);
    try {
      const body = await devApi<{ ticket: string }>("/api/developers/email/otp/verify", {
        method: "POST",
        body: JSON.stringify({ otp: code }),
      });
      setTicket(body.ticket);
      setOtp("");
      setExpiresAt(null);
      setOk(null);
      setStep("new_email");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not verify code");
    } finally {
      setBusy(false);
    }
  };

  const requestNewEmailOtp = async () => {
    const email = newEmail.trim();
    if (!isValidEmail(email)) {
      setError("Enter a valid email address.");
      return;
    }
    if (email.toLowerCase() === currentEmail.toLowerCase()) {
      setError("That's already your email address.");
      return;
    }
    if (!ticket) {
      setError("Current email verification expired. Start again.");
      setStep("current");
      return;
    }
    setBusy(true);
    setError(null);
    setOk(null);
    try {
      const body = await devApi<OtpSendResult>("/api/developers/email", {
        method: "POST",
        body: JSON.stringify({ ticket, email }),
      });
      rememberSend(body);
      setOtp("");
      setStep("new_otp");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not send verification code");
    } finally {
      setBusy(false);
    }
  };

  const sendNewEmailOtp = async (ev: FormEvent) => {
    ev.preventDefault();
    await requestNewEmailOtp();
  };

  const verifyNewEmailOtp = async (ev: FormEvent) => {
    ev.preventDefault();
    const code = otp.replace(/\D/g, "");
    if (code.length !== 6) {
      setError("Enter the 6-digit verification code.");
      return;
    }
    if (expiresIn <= 0) {
      setError("That code has expired. Request a new one.");
      return;
    }
    if (!ticket) {
      setError("Current email verification expired. Start again.");
      setStep("current");
      return;
    }
    setBusy(true);
    setError(null);
    setOk(null);
    try {
      const body = await devApi<{ ticket: string; email?: string }>("/api/developers/email/verify", {
        method: "POST",
        body: JSON.stringify({ ticket, otp: code }),
      });
      setConfirmTicket(body.ticket);
      if (body.email) setNewEmail(body.email);
      setOtp("");
      setExpiresAt(null);
      setOk("New email verified. Confirm to update your account.");
      setStep("confirm");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not verify the new email");
    } finally {
      setBusy(false);
    }
  };

  const confirmNewEmail = async (ev: FormEvent) => {
    ev.preventDefault();
    if (!confirmTicket) {
      setError("New email verification expired. Start again.");
      setStep("current");
      return;
    }
    setBusy(true);
    setError(null);
    setOk(null);
    try {
      const body = await devApi<{ developer: Developer }>("/api/developers/email/confirm", {
        method: "POST",
        body: JSON.stringify({ ticket: confirmTicket }),
      });
      onAccountUpdated?.(body.developer);
      setUpdatedEmail(body.developer.email);
      void updateNativeEmail(body.developer.email);
      setStep("done");
      setOk("Email updated.");
      setOtp("");
      setTicket("");
      setConfirmTicket("");
      setExpiresAt(null);
      setResendAt(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update email");
    } finally {
      setBusy(false);
    }
  };

  const restart = () => {
    setStep("current");
    setOtp("");
    setNewEmail("");
    setTicket("");
    setConfirmTicket("");
    setMasked(updatedEmail);
    setError(null);
    setOk(null);
    setExpiresAt(null);
    setResendAt(null);
  };

  const stepIndex = ["current", "current_otp", "new_email", "new_otp", "confirm", "done"].indexOf(step);

  return (
    <>
      <ol className="email-step" aria-hidden="true">
        {["current", "current_otp", "new_email", "new_otp", "confirm"].map((id, idx) => (
          <li key={id} className={idx < stepIndex ? "is-done" : idx === stepIndex ? "is-on" : ""} />
        ))}
      </ol>

      {step === "current" && (
        <form
          key="current"
          className="device-form"
          onSubmit={(ev) => {
            ev.preventDefault();
            void sendCurrentOtp();
          }}
        >
          <p className="devportal-hint">
            Your current address is filled in. Tap Get Code to send a verification code to this inbox.
          </p>
          <label>
            Current email
            <div className="device-form-value">{currentEmail || "Loading…"}</div>
          </label>
          {error && <p className="form-error">{error}</p>}
          {ok && <p className="form-success">{ok}</p>}
          <button type="submit" className="primary" disabled={busy || !currentEmail}>
            {busy ? "Sending…" : "Get Code"}
          </button>
        </form>
      )}

      {step === "current_otp" && (
        <form key="current_otp" className="device-form" onSubmit={(ev) => void verifyCurrentOtp(ev)}>
          <p className="devportal-hint">Enter the code sent to {currentEmail}.</p>
          <label>
            Current email
            <div className="device-form-value">{currentEmail}</div>
          </label>
          <label>
            Verification code
            <OtpInput
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              required
            />
          </label>
          <p className="devportal-hint">
            {expiresIn > 0 ? `Code expires in ${formatMmSs(expiresIn)}.` : "Code expired. Resend to continue."}
          </p>
          {error && <p className="form-error">{error}</p>}
          {ok && <p className="form-success">{ok}</p>}
          <button type="submit" className="primary" disabled={busy || otp.length !== 6 || expiresIn <= 0}>
            {busy ? "Verifying…" : "Continue"}
          </button>
          <button type="button" className="form-link" disabled={busy || resendIn > 0} onClick={() => void resendCurrentOtp()}>
            {resendIn > 0 ? `Resend code in ${formatMmSs(resendIn)}` : "Resend code"}
          </button>
        </form>
      )}

      {step === "new_email" && (
        <form key="new_email" className="device-form" onSubmit={(ev) => void sendNewEmailOtp(ev)}>
          <p className="devportal-hint">
            Enter the new email, then tap Get OTP. We’ll send a code to that address. Your account email changes only after you confirm.
          </p>
          <label>
            New email
            <EmailInput
              autoComplete="email"
              autoFocusOnMount
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              required
            />
          </label>
          {error && <p className="form-error">{error}</p>}
          {ok && <p className="form-success">{ok}</p>}
          <button type="submit" className="primary" disabled={busy}>
            {busy ? "Sending…" : "Get OTP"}
          </button>
        </form>
      )}

      {step === "new_otp" && (
        <form key="new_otp" className="device-form" onSubmit={(ev) => void verifyNewEmailOtp(ev)}>
          <p className="devportal-hint">Enter the code sent to {newEmail.trim() || masked}.</p>
          <label>
            New email
            <div className="device-form-value">{newEmail.trim() || masked}</div>
          </label>
          <label>
            OTP
            <OtpInput
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              required
            />
          </label>
          <p className="devportal-hint">
            {expiresIn > 0 ? `Code expires in ${formatMmSs(expiresIn)}.` : "Code expired. Resend to continue."}
          </p>
          {error && <p className="form-error">{error}</p>}
          {ok && <p className="form-success">{ok}</p>}
          <button type="submit" className="primary" disabled={busy || otp.length !== 6 || expiresIn <= 0}>
            {busy ? "Verifying…" : "Continue"}
          </button>
          <button
            type="button"
            className="form-link"
            disabled={busy || resendIn > 0}
            onClick={() => void requestNewEmailOtp()}
          >
            {resendIn > 0 ? `Resend OTP in ${formatMmSs(resendIn)}` : "Resend OTP"}
          </button>
        </form>
      )}

      {step === "confirm" && (
        <form key="confirm" className="device-form" onSubmit={(ev) => void confirmNewEmail(ev)}>
          <p className="devportal-hint">
            Both addresses are verified. Confirm to permanently update your account email to the address below.
          </p>
          <label>
            Current email
            <div className="device-form-value">{currentEmail}</div>
          </label>
          <label>
            New email
            <div className="device-form-value">{newEmail.trim()}</div>
          </label>
          {error && <p className="form-error">{error}</p>}
          {ok && <p className="form-success">{ok}</p>}
          <button type="submit" className="primary" disabled={busy || !confirmTicket}>
            {busy ? "Updating…" : "Confirm"}
          </button>
        </form>
      )}

      {step === "done" && (
        <div className="device-form">
          <p className="form-success">Your email is now {updatedEmail}.</p>
          <p className="devportal-hint">Use this address the next time you sign in.</p>
          <button type="button" className="primary" onClick={() => (onDone ? onDone() : restart())}>
            Done
          </button>
        </div>
      )}
    </>
  );
}

function MobileSettingsMenu({ go, email, role }: { go: (to: string) => void; email: string; role: Developer["role"] }) {
  const isSuperAdmin = role === "superadmin";
  return (
    <div className="mobile-page">
      <p className="mobile-page-lead">
        {isSuperAdmin
          ? "Account, developer tools, and diagnostics. Open a row to manage it."
          : "Manage your account and security settings."}
      </p>
      <BiometricSecuritySection email={email} />
      <div className="settings-group">
        <p className="settings-group-label">Account</p>
        <button type="button" className="settings-row" onClick={() => go("/settings/password")}>
          <span className="settings-row-icon">
            <IconLock />
          </span>
          <span className="settings-row-copy">
            <strong>Change password</strong>
            <em>Update the Master account password</em>
          </span>
          <IconChevron className="settings-row-chevron" />
        </button>
        <button type="button" className="settings-row" onClick={() => go("/settings/email")}>
          <span className="settings-row-icon">
            <IconMail />
          </span>
          <span className="settings-row-copy">
            <strong>Change email</strong>
            <em>Verify the current address, then set a new one</em>
          </span>
          <IconChevron className="settings-row-chevron" />
        </button>
        {isSuperAdmin ? (
          <>
            <button type="button" className="settings-row" onClick={() => go("/users")}>
              <span className="settings-row-icon">
                <IconUsers />
              </span>
              <span className="settings-row-copy">
                <strong>Users</strong>
                <em>Manage users, cameras, and API keys</em>
              </span>
              <IconChevron className="settings-row-chevron" />
            </button>
            <button type="button" className="settings-row" onClick={() => go("/settings/smtp")}>
              <span className="settings-row-icon">
                <IconMail />
              </span>
              <span className="settings-row-copy">
                <strong>SMTP / Email Configuration</strong>
                <em>Set the mail server used for OTPs and notifications</em>
              </span>
              <IconChevron className="settings-row-chevron" />
            </button>
          </>
        ) : null}
      </div>
      {isSuperAdmin ? (
        <>
          <div className="settings-group">
            <p className="settings-group-label">Developer</p>
            <button type="button" className="settings-row" onClick={() => go("/developer")}>
              <span className="settings-row-icon">
                <IconOverview />
              </span>
              <span className="settings-row-copy">
                <strong>Developer overview</strong>
                <em>Platform status, keys, and cameras</em>
              </span>
              <IconChevron className="settings-row-chevron" />
            </button>
            <button type="button" className="settings-row" onClick={() => go("/developer/api-keys")}>
              <span className="settings-row-icon">
                <IconKey />
              </span>
              <span className="settings-row-copy">
                <strong>API Keys</strong>
                <em>Create, rotate, and revoke keys</em>
              </span>
              <IconChevron className="settings-row-chevron" />
            </button>
            <button type="button" className="settings-row" onClick={() => go("/developer/docs")}>
              <span className="settings-row-icon">
                <IconDocs />
              </span>
              <span className="settings-row-copy">
                <strong>API Documentation</strong>
                <em>PDF, HTML, and OpenAPI</em>
              </span>
              <IconChevron className="settings-row-chevron" />
            </button>
            <button type="button" className="settings-row" onClick={() => go("/developer/examples")}>
              <span className="settings-row-icon">
                <IconCode />
              </span>
              <span className="settings-row-copy">
                <strong>Integration examples</strong>
                <em>cURL, web, and Flutter snippets</em>
              </span>
              <IconChevron className="settings-row-chevron" />
            </button>
            <button type="button" className="settings-row" onClick={() => go("/sdk")}>
              <span className="settings-row-icon">
                <IconSdk />
              </span>
              <span className="settings-row-copy">
                <strong>SDKs</strong>
                <em>Web and Flutter packages</em>
              </span>
              <IconChevron className="settings-row-chevron" />
            </button>
          </div>
          <div className="settings-group">
            <p className="settings-group-label">Diagnostics</p>
            <button type="button" className="settings-row" onClick={() => go("/dev")}>
              <span className="settings-row-icon">
                <IconLogs />
              </span>
              <span className="settings-row-copy">
                <strong>Dev logs</strong>
                <em>Live API, JT808, and media logs</em>
              </span>
              <IconChevron className="settings-row-chevron" />
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}

export function SettingsPage({
  me,
  native = false,
  section = "all",
  go,
  onAccountUpdated,
}: {
  me: Developer;
  native?: boolean;
  section?: "all" | "menu" | "password" | "email" | "smtp";
  go?: (to: string) => void;
  onAccountUpdated?: (developer: Developer) => void;
}) {
  if (native && section === "menu" && go) {
    return <MobileSettingsMenu go={go} email={me.email} role={me.role} />;
  }

  if (native && section === "password") {
    return (
      <div className="mobile-page">
        <PasswordForm onPasswordUpdated={(password) => void updateNativePassword(password)} />
      </div>
    );
  }

  if (native && section === "email") {
    return (
      <div className="mobile-page">
        <EmailForm me={me} onAccountUpdated={onAccountUpdated} onDone={() => go?.("/profile")} />
      </div>
    );
  }

  if (native && section === "smtp") {
    return (
      <div className="mobile-page">
        <SmtpSettingsForm me={me} />
      </div>
    );
  }

  return (
    <div className="devportal-main">
      <div className="devportal-grid">
        <section className="devportal-card">
          <h2>Master account</h2>
          <p>
            <strong>{me.name}</strong>
          </p>
          <p>{me.email}</p>
          <p>Role: {me.role === "superadmin" ? "Super Admin" : "User"}</p>
          <p>Status: {me.status}</p>
        </section>
        <section className="devportal-card">
          <h2>Change password</h2>
          <PasswordForm />
        </section>
        {me.role === "superadmin" ? (
          <section className="devportal-card">
            <h2>SMTP / Email Configuration</h2>
            <SmtpSettingsForm me={me} />
          </section>
        ) : null}
      </div>
    </div>
  );
}
